import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../supabaseClient';
import './WritingPromptManager.css';

const WRITING_TYPES = ['narrative', 'persuasive', 'informative', 'creative', 'descriptive', 'advisory'];
const TIERS = ['foundation', 'intermediate', 'advanced', 'mastery'];
const YEAR_LEVELS = [4, 5, 6];

const EMPTY_DRAFT = {
  prompt_text: '',
  writing_type: 'narrative',
  tier: 'foundation',
  year_level: 5,
  instructions: '',
  min_words: 250,
  max_words: 400,
  time_limit_minutes: 30,
  is_active: true
};

const WritingPromptManager = () => {
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    writing_type: '',
    tier: '',
    year_level: '',
    show_inactive: true
  });
  const [editing, setEditing] = useState(null); // null | 'new' | promptId
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [saving, setSaving] = useState(false);

  const apiBase = process.env.REACT_APP_BACKEND_URL;

  const authedFetch = useCallback(async (path, options = {}) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Not authenticated');
    return fetch(`${apiBase}${path}`, {
      ...options,
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
        ...(options.headers || {})
      }
    });
  }, [apiBase]);

  const loadPrompts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams();
      if (filters.writing_type) params.append('writing_type', filters.writing_type);
      if (filters.tier) params.append('tier', filters.tier);
      if (filters.year_level) params.append('year_level', filters.year_level);
      params.append('include_inactive', filters.show_inactive ? 'true' : 'false');

      const res = await authedFetch(`/api/selective/admin/writing-prompts?${params}`);
      if (!res.ok) throw new Error(`Failed to load prompts (${res.status})`);
      const data = await res.json();
      setPrompts(data.prompts || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [filters, authedFetch]);

  useEffect(() => {
    loadPrompts();
  }, [loadPrompts]);

  const startNew = () => {
    setDraft(EMPTY_DRAFT);
    setEditing('new');
  };

  const startEdit = (prompt) => {
    setDraft({
      prompt_text: prompt.prompt_text || '',
      writing_type: prompt.writing_type || 'narrative',
      tier: prompt.tier || 'foundation',
      year_level: prompt.year_level || 5,
      instructions: prompt.instructions || '',
      min_words: prompt.min_words || 250,
      max_words: prompt.max_words || 400,
      time_limit_minutes: prompt.time_limit_minutes || 30,
      is_active: prompt.is_active !== false
    });
    setEditing(prompt.id);
  };

  const cancelEdit = () => {
    setEditing(null);
    setDraft(EMPTY_DRAFT);
  };

  const saveDraft = async () => {
    if (draft.prompt_text.trim().length < 10) {
      alert('Prompt text must be at least 10 characters.');
      return;
    }
    if (Number(draft.max_words) < Number(draft.min_words)) {
      alert('max_words must be ≥ min_words.');
      return;
    }
    try {
      setSaving(true);
      const body = {
        prompt_text: draft.prompt_text.trim(),
        writing_type: draft.writing_type,
        tier: draft.tier,
        year_level: Number(draft.year_level),
        instructions: draft.instructions.trim() || null,
        min_words: Number(draft.min_words),
        max_words: Number(draft.max_words),
        time_limit_minutes: Number(draft.time_limit_minutes),
        is_active: !!draft.is_active
      };

      const res = editing === 'new'
        ? await authedFetch('/api/selective/admin/writing-prompts', {
            method: 'POST',
            body: JSON.stringify(body)
          })
        : await authedFetch(`/api/selective/admin/writing-prompts/${editing}`, {
            method: 'PUT',
            body: JSON.stringify(body)
          });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || `Save failed (${res.status})`);
      }
      await loadPrompts();
      cancelEdit();
    } catch (e) {
      alert(`Save failed: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const softDelete = async (prompt) => {
    if (!window.confirm(`Mark "${prompt.prompt_text.slice(0, 60)}…" as inactive? Students will no longer see it.`)) return;
    try {
      const res = await authedFetch(`/api/selective/admin/writing-prompts/${prompt.id}`, {
        method: 'DELETE'
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || `Delete failed (${res.status})`);
      }
      await loadPrompts();
    } catch (e) {
      alert(`Delete failed: ${e.message}`);
    }
  };

  const reactivate = async (prompt) => {
    try {
      const res = await authedFetch(`/api/selective/admin/writing-prompts/${prompt.id}`, {
        method: 'PUT',
        body: JSON.stringify({ is_active: true })
      });
      if (!res.ok) throw new Error(`Reactivate failed (${res.status})`);
      await loadPrompts();
    } catch (e) {
      alert(`Reactivate failed: ${e.message}`);
    }
  };

  return (
    <div className="wpm-container">
      <div className="wpm-header">
        <div>
          <h2>✍️ Writing Prompts</h2>
          <p className="wpm-subtitle">{prompts.length} prompt(s) shown</p>
        </div>
        <button className="wpm-btn-primary" onClick={startNew} disabled={editing !== null}>
          + New Prompt
        </button>
      </div>

      <div className="wpm-filters">
        <select
          value={filters.writing_type}
          onChange={e => setFilters({ ...filters, writing_type: e.target.value })}
        >
          <option value="">All types</option>
          {WRITING_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <select
          value={filters.tier}
          onChange={e => setFilters({ ...filters, tier: e.target.value })}
        >
          <option value="">All tiers</option>
          {TIERS.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <select
          value={filters.year_level}
          onChange={e => setFilters({ ...filters, year_level: e.target.value })}
        >
          <option value="">All years</option>
          {YEAR_LEVELS.map(y => <option key={y} value={y}>Year {y}</option>)}
        </select>
        <label className="wpm-checkbox">
          <input
            type="checkbox"
            checked={filters.show_inactive}
            onChange={e => setFilters({ ...filters, show_inactive: e.target.checked })}
          />
          Show inactive
        </label>
      </div>

      {editing !== null && (
        <PromptEditor
          isNew={editing === 'new'}
          draft={draft}
          setDraft={setDraft}
          onSave={saveDraft}
          onCancel={cancelEdit}
          saving={saving}
        />
      )}

      {loading && <div className="wpm-loading">Loading prompts…</div>}
      {error && <div className="wpm-error">Error: {error} <button onClick={loadPrompts}>Retry</button></div>}

      {!loading && !error && (
        <div className="wpm-table">
          <table>
            <thead>
              <tr>
                <th>Prompt</th>
                <th>Type</th>
                <th>Tier</th>
                <th>Year</th>
                <th>Words</th>
                <th>Time</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {prompts.length === 0 && (
                <tr><td colSpan="8" className="wpm-empty">No prompts match your filters.</td></tr>
              )}
              {prompts.map(p => (
                <tr key={p.id} className={p.is_active === false ? 'inactive' : ''}>
                  <td className="wpm-prompt-cell">{p.prompt_text}</td>
                  <td><span className="wpm-tag">{p.writing_type}</span></td>
                  <td>{p.tier}</td>
                  <td>{p.year_level}</td>
                  <td>{p.min_words}–{p.max_words}</td>
                  <td>{p.time_limit_minutes}m</td>
                  <td>
                    {p.is_active === false
                      ? <span className="wpm-status inactive">Inactive</span>
                      : <span className="wpm-status active">Active</span>}
                  </td>
                  <td className="wpm-actions">
                    <button onClick={() => startEdit(p)} disabled={editing !== null}>Edit</button>
                    {p.is_active === false
                      ? <button onClick={() => reactivate(p)} disabled={editing !== null}>Reactivate</button>
                      : <button className="danger" onClick={() => softDelete(p)} disabled={editing !== null}>Deactivate</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const PromptEditor = ({ isNew, draft, setDraft, onSave, onCancel, saving }) => {
  const set = (k, v) => setDraft(d => ({ ...d, [k]: v }));

  return (
    <div className="wpm-editor">
      <div className="wpm-editor-header">
        <h3>{isNew ? 'New Writing Prompt' : 'Edit Writing Prompt'}</h3>
      </div>

      <div className="wpm-form">
        <label className="wpm-field full">
          <span>Prompt text</span>
          <textarea
            rows={4}
            value={draft.prompt_text}
            onChange={e => set('prompt_text', e.target.value)}
            placeholder="Write the essay prompt students will see…"
          />
        </label>

        <label className="wpm-field">
          <span>Writing type</span>
          <select value={draft.writing_type} onChange={e => set('writing_type', e.target.value)}>
            {WRITING_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </label>

        <label className="wpm-field">
          <span>Tier</span>
          <select value={draft.tier} onChange={e => set('tier', e.target.value)}>
            {TIERS.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </label>

        <label className="wpm-field">
          <span>Year level</span>
          <select value={draft.year_level} onChange={e => set('year_level', Number(e.target.value))}>
            {YEAR_LEVELS.map(y => <option key={y} value={y}>Year {y}</option>)}
          </select>
        </label>

        <label className="wpm-field">
          <span>Min words</span>
          <input type="number" min="50" max="2000" value={draft.min_words} onChange={e => set('min_words', e.target.value)} />
        </label>

        <label className="wpm-field">
          <span>Max words</span>
          <input type="number" min="50" max="2000" value={draft.max_words} onChange={e => set('max_words', e.target.value)} />
        </label>

        <label className="wpm-field">
          <span>Time (minutes)</span>
          <input type="number" min="5" max="120" value={draft.time_limit_minutes} onChange={e => set('time_limit_minutes', e.target.value)} />
        </label>

        <label className="wpm-field full">
          <span>Instructions (optional)</span>
          <textarea
            rows={2}
            value={draft.instructions}
            onChange={e => set('instructions', e.target.value)}
            placeholder="Optional extra instructions shown alongside the prompt"
          />
        </label>

        <label className="wpm-field wpm-checkbox-field">
          <input
            type="checkbox"
            checked={draft.is_active}
            onChange={e => set('is_active', e.target.checked)}
          />
          <span>Active (visible to students)</span>
        </label>
      </div>

      <div className="wpm-editor-actions">
        <button className="wpm-btn-secondary" onClick={onCancel} disabled={saving}>Cancel</button>
        <button className="wpm-btn-primary" onClick={onSave} disabled={saving}>
          {saving ? 'Saving…' : (isNew ? 'Create' : 'Save changes')}
        </button>
      </div>
    </div>
  );
};

export default WritingPromptManager;
